import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import app, { auth } from "../../firebaseConfig";
import {
  buildCompanyRestrictionMessage,
  deriveCompanyAccessState,
  startCompanyAccessMonitor,
} from "../../utils/companyAccess";

const POLL_INTERVAL_MS = 15000;

export default function AccessRestrictedPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fallbackReason = useMemo(
    () =>
      searchParams.get("reason") ||
      "Your company access is currently restricted. Please contact support.",
    [searchParams]
  );
  const [reason, setReason] = useState(
    fallbackReason
  );
  const returnTo = useMemo(
    () => searchParams.get("returnTo") || "/company-dashboard",
    [searchParams]
  );

  useEffect(() => {
    const firestore = getFirestore(app);
    let cancelled = false;
    let stopMonitor = () => {};
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      const userSnap = await getDoc(doc(firestore, "users", user.uid));
      const userData = userSnap.exists() ? userSnap.data() : null;
      const companyKey =
        userData?.companyName || userData?.company || userData?.company_key;
      if (!companyKey) return;

      const checkAccess = async () => {
        try {
          const candidates = Array.from(
            new Set(
              [companyKey, String(companyKey || "").replace(/\//g, "-")].filter(Boolean)
            )
          );
          let permissions = null;
          for (const candidate of candidates) {
            const companySnap = await getDoc(doc(firestore, "companies", candidate));
            if (!companySnap.exists()) continue;
            const subscriptionSnap = await getDoc(
              doc(firestore, "companies", candidate, "subscriptionSnapshots", "current")
            );
            permissions = deriveCompanyAccessState({
              ...companySnap.data(),
              ...(subscriptionSnap.exists() ? subscriptionSnap.data() : {}),
            });
            break;
          }
          if (!permissions) {
            return {
              restricted: true,
              reason:
                "Your company has not been set up for subscription access. Please contact support.",
            };
          }
          return {
            restricted: !permissions.can_operate,
            reason: buildCompanyRestrictionMessage(permissions),
          };
        } catch (_error) {
          return { restricted: true, reason: fallbackReason };
        }
      };

      const result = await checkAccess();
      if (cancelled) return;
      if (!result.restricted) {
        navigate(returnTo, { replace: true });
        return;
      }
      setReason(result.reason);

      stopMonitor();

      stopMonitor = startCompanyAccessMonitor({
        monitorKey: `restricted-${user.uid}`,
        checkAccess,
        onRestricted: (nextReason) => setReason(nextReason),
        onRestored: () => navigate(returnTo, { replace: true }),
        intervalMs: POLL_INTERVAL_MS,
      });
    });

    return () => {
      cancelled = true;
      unsubscribeAuth();
      stopMonitor();
    };
  }, [fallbackReason, navigate, returnTo]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-xl w-full bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Access restricted</h1>
        <p className="text-slate-600">
          You cannot access your dashboard right now.
        </p>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {reason}
        </div>
        <p className="text-xs text-slate-500">
          We are checking your access automatically. This page closes once your
          subscription or status is restored.
        </p>
        <Link
          to="/login"
          className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-white text-sm font-semibold hover:bg-blue-700"
        >
          Go to login
        </Link>
      </div>
    </div>
  );
}
